/**
 * MockupTemplatesPage — bibliothèque de templates de mockups (S4) — Direction C.
 *
 * Promue depuis l'onglet Réglages > Templates mockup en entrée sidebar
 * primaire : ce n'est pas un simple catalogue de valeurs (comme formats /
 * encadrements / supports) mais un espace de gestion d'actifs créatifs
 * (upload PSDs en masse, MaskZoneEditor pour édition mask, sync Vision
 * Tier 1, matching œuvre↔template pour le PublishWizard S6).
 *
 * Header Direction C :
 *   - Eyebrow « Bibliothèque · N templates »
 *   - Titre « Mockups »
 *   - Actions : <SearchInput> inline + bouton « + Ajouter des templates »
 *
 * **PSD only** : depuis 2026-05-09, on n'accepte plus l'upload d'images
 * plates (JPG/PNG/WebP) côté FE — l'usage industriel (packs fournisseur)
 * est en PSD avec Smart Object MULTIPLY, et `psd_extractor` auto-extrait
 * la mask_zone. L'édition manuelle de `mask_zone` reste disponible sur
 * un template existant via `MockupTemplateDetailModal` + `MaskZoneEditor`.
 *
 * Sous le header, une rangée :
 *   - Tabs visibilité (Tous / Actifs / Désactivés) — filtrent côté client
 *     sur `template.active` (soft-delete).
 *   - Sélects « Pièce » + « Cadre » à droite, étiquetés en mono caps.
 *
 * Grille de cards thumbnail. Click card → modal détail.
 */

import { useMemo, useState } from 'react'
import { Image as ImageIcon, Plus, Sparkles } from 'lucide-react'

import AspectRatioLabel from '@/components/ui/AspectRatioLabel'
import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import SearchInput from '@/components/ui/SearchInput'
import Select from '@/components/ui/Select'
import Tabs from '@/components/ui/Tabs'
import MockupTemplateBulkUploadModal from '@/components/mockup-templates/MockupTemplateBulkUploadModal'
import MockupTemplateDetailModal from '@/components/mockup-templates/MockupTemplateDetailModal'
import Page from '@/components/layout/Page'
import { useFormats } from '@/hooks/useCatalog'
import { useMockupTemplates } from '@/hooks/useMockupTemplates'
import {
  FRAME_STYLE_FR,
  FRAME_STYLE_OPTIONS,
  MOCKUP_TEMPLATE_KIND_FR,
  ROOM_TYPE_FR,
  ROOM_TYPE_OPTIONS,
  type FrameStyle,
  type MockupTemplateSummaryResponse,
  type RoomType,
} from '@/types/mockup-templates'
import { ratioString } from '@/utils/formatLabel'
import { findCompatibleFormats, type FormatLite } from '@/utils/mockups/maskAspectRatio'

import './MockupTemplatesPage.css'

type VisibilityTab = 'all' | 'active' | 'inactive'

export default function MockupTemplatesPage() {
  const [q, setQ] = useState('')
  const [roomType, setRoomType] = useState<RoomType | ''>('')
  const [frameStyle, setFrameStyle] = useState<FrameStyle | ''>('')
  const [ratio, setRatio] = useState<string>('')
  const [visibility, setVisibility] = useState<VisibilityTab>('all')
  const [bulkOpen, setBulkOpen] = useState(false)
  const [openTemplateId, setOpenTemplateId] = useState<number | null>(null)

  const params = {
    q: q.trim() || undefined,
    room_type: roomType || undefined,
    frame_style: frameStyle || undefined,
  }
  const templatesQuery = useMockupTemplates(params)
  const formatsQuery = useFormats({ only_active: true })
  const formats = (formatsQuery.data ?? []) as FormatLite[]
  const allTemplates = useMemo(() => templatesQuery.data ?? [], [templatesQuery.data])

  // Options ratio dérivées des templates chargés : on snap chaque ratio sur
  // la même grammaire que `<AspectRatioLabel>` (noble ratios + fallback
  // décimal) et on regroupe. Tri par ratio numérique croissant : portraits
  // d'abord, carré, puis paysages — l'œil descend la liste comme une
  // boussole d'orientation.
  const ratioOptions = useMemo(() => {
    const groups = new Map<string, { count: number; sample: number }>()
    for (const t of allTemplates) {
      const label = ratioString(t.mask_aspect_ratio, 1)
      const existing = groups.get(label)
      if (existing) {
        existing.count += 1
      } else {
        groups.set(label, { count: 1, sample: t.mask_aspect_ratio })
      }
    }
    return Array.from(groups.entries())
      .map(([label, { count, sample }]) => ({ label, count, sample }))
      .sort((a, b) => a.sample - b.sample)
  }, [allTemplates])

  // Filtre client-side : `active` (soft-delete) + `ratio` snappé. Les filtres
  // BE (q/room_type/frame_style) sont déjà appliqués via la query.
  const templates = useMemo(() => {
    let list = allTemplates
    if (ratio) {
      list = list.filter((t) => ratioString(t.mask_aspect_ratio, 1) === ratio)
    }
    if (visibility === 'active') list = list.filter((t) => t.active)
    else if (visibility === 'inactive') list = list.filter((t) => !t.active)
    return list
  }, [allTemplates, ratio, visibility])

  const isEmpty = !templatesQuery.isLoading && templates.length === 0
  const noResults = isEmpty && (q || roomType || frameStyle || ratio || visibility !== 'all')

  const count = allTemplates.length
  const eyebrow = `Bibliothèque · ${count} template${count > 1 ? 's' : ''}`

  const visibilityTabItems = [
    { value: 'all' as VisibilityTab, label: 'Tous' },
    { value: 'active' as VisibilityTab, label: 'Actifs' },
    { value: 'inactive' as VisibilityTab, label: 'Désactivés' },
  ]

  return (
    <Page width="wide">
      <Page.Header
        eyebrow={eyebrow}
        title="Mockups"
        actions={
          <div className="mockup-templates-header-actions">
            <SearchInput
              value={q}
              onChange={setQ}
              placeholder="Rechercher un template…"
              ariaLabel="Rechercher un template"
              className="mockup-templates-header-search"
            />
            <Button variant="primary" onClick={() => setBulkOpen(true)}>
              <Plus aria-hidden="true" size={14} />
              Ajouter des templates
            </Button>
          </div>
        }
      />

      <div className="mockup-templates-tabs-row">
        <Tabs<VisibilityTab>
          value={visibility}
          onChange={setVisibility}
          items={visibilityTabItems}
          ariaLabel="Filtrer par visibilité"
        />
        <div className="mockup-templates-tabs-controls">
          <label className="mockup-templates-control">
            <span className="mockup-templates-control-label">Pièce</span>
            <Select
              value={roomType}
              onChange={(e) => setRoomType(e.target.value as RoomType | '')}
              aria-label="Filtrer par pièce"
            >
              <option value="">Toutes pièces</option>
              {ROOM_TYPE_OPTIONS.map((rt) => (
                <option key={rt} value={rt}>
                  {ROOM_TYPE_FR[rt]}
                </option>
              ))}
            </Select>
          </label>
          <label className="mockup-templates-control">
            <span className="mockup-templates-control-label">Cadre</span>
            <Select
              value={frameStyle}
              onChange={(e) => setFrameStyle(e.target.value as FrameStyle | '')}
              aria-label="Filtrer par cadre"
            >
              <option value="">Tous cadres</option>
              {FRAME_STYLE_OPTIONS.map((fs) => (
                <option key={fs} value={fs}>
                  {FRAME_STYLE_FR[fs]}
                </option>
              ))}
            </Select>
          </label>
          <label className="mockup-templates-control">
            <span className="mockup-templates-control-label">Ratio</span>
            <Select
              value={ratio}
              onChange={(e) => setRatio(e.target.value)}
              aria-label="Filtrer par ratio"
              disabled={ratioOptions.length === 0}
            >
              <option value="">Tous ratios</option>
              {ratioOptions.map((opt) => (
                <option key={opt.label} value={opt.label}>
                  {opt.label} · {opt.count}
                </option>
              ))}
            </Select>
          </label>
        </div>
      </div>

      {isEmpty && !noResults ? (
        <EmptyState
          title="Aucun template dans ta bibliothèque"
          description="Commence par ajouter quelques templates : cadre vide sur fond neutre, intérieur stylisé, flat lay. Vision étiquettera automatiquement chacun pour le matching publication."
          action={
            <Button variant="primary" onClick={() => setBulkOpen(true)}>
              <ImageIcon size={16} aria-hidden="true" />
              Ajouter le premier template
            </Button>
          }
        />
      ) : null}

      {noResults ? (
        <EmptyState
          title="Aucun résultat"
          description="Essaie de relâcher un filtre ou de simplifier la recherche."
        />
      ) : null}

      {templates.length > 0 ? (
        <div className="mockup-templates-grid">
          {templates.map((tpl) => (
            <TemplateCard
              key={tpl.id}
              template={tpl}
              formats={formats}
              onClick={() => setOpenTemplateId(tpl.id)}
            />
          ))}
        </div>
      ) : null}

      {bulkOpen ? (
        <MockupTemplateBulkUploadModal isOpen onClose={() => setBulkOpen(false)} />
      ) : null}

      {openTemplateId !== null ? (
        <MockupTemplateDetailModal
          templateId={openTemplateId}
          onClose={() => setOpenTemplateId(null)}
        />
      ) : null}
    </Page>
  )
}

interface TemplateCardProps {
  template: MockupTemplateSummaryResponse
  formats: FormatLite[]
  onClick: () => void
}

function TemplateCard({ template, formats, onClick }: TemplateCardProps) {
  // `image_url` est présigné server-side au GET — pas de round-trip FE
  // (le BE résout `presigned_url(minio_key)` en local MinIO, ~1ms par row).
  const thumbUrl = template.image_url
  const compatibleCount = findCompatibleFormats(template.mask_aspect_ratio, formats).length

  return (
    <div
      role="button"
      tabIndex={0}
      className="mockup-template-card"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      data-inactive={!template.active || undefined}
    >
      <div className="mockup-template-card-image">
        {thumbUrl ? (
          <img src={thumbUrl} alt={template.name} loading="lazy" decoding="async" />
        ) : (
          <div className="mockup-template-card-placeholder">
            <ImageIcon size={32} aria-hidden="true" />
          </div>
        )}
        {template.vision_extracted_at ? (
          <span className="mockup-template-card-vision-badge" title="Vision Tier 1 extraite">
            <Sparkles size={11} aria-hidden="true" />
          </span>
        ) : null}
      </div>
      <div className="mockup-template-card-body">
        <h4 className="mockup-template-card-name">{template.name}</h4>
        <p className="mockup-template-card-kind">{MOCKUP_TEMPLATE_KIND_FR[template.kind]}</p>
        <div className="mockup-template-card-chips">
          {template.room_type ? (
            <Badge variant="default">{ROOM_TYPE_FR[template.room_type]}</Badge>
          ) : null}
          {template.frame_style ? (
            <Badge variant="default">{FRAME_STYLE_FR[template.frame_style]}</Badge>
          ) : null}
          {formats.length > 0 ? (
            <Badge
              variant={compatibleCount > 0 ? 'default' : 'warning'}
              title={`Ratio ${template.mask_aspect_ratio.toFixed(3)} · ${compatibleCount} format(s) compatible(s)`}
            >
              <AspectRatioLabel value={template.mask_aspect_ratio} /> · {compatibleCount} fmt
            </Badge>
          ) : null}
          {(template.tag_count ?? 0) > 0 ? (
            <Badge variant="info">{template.tag_count} tags</Badge>
          ) : null}
          {!template.active ? <Badge variant="default">Désactivé</Badge> : null}
        </div>
      </div>
    </div>
  )
}
